import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  Image,
  useColorModeValue,
  Icon,
  HStack,
  Badge,
  Divider,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FiCheck, FiTarget, FiUsers, FiAward, FiShield, FiTrendingUp } from 'react-icons/fi';

const MotionBox = motion(Box);

const StatBox = ({ number, label, icon }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      bg={bgColor}
      p={8}
      borderRadius="xl"
      shadow="xl"
      textAlign="center"
      sx={{
        transition: 'all 0.3s',
        _hover: { 
          transform: 'translateY(-5px)', 
          boxShadow: '2xl' 
        }
      }}
    >
      <Icon as={icon} w={12} h={12} color="brand.500" mb={4} />
      <Text fontSize="4xl" fontWeight="bold" color="brand.500" mb={2}>
        {number}
      </Text>
      <Text color={useColorModeValue('gray.600', 'gray.300')} fontSize="lg">
        {label}
      </Text>
    </MotionBox>
  );
};

const About = () => {
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const gradientText = useColorModeValue(
    'linear(to-r, brand.500, brand.600)',
    'linear(to-r, brand.400, brand.500)'
  );

  const stats = [
    { number: '1000+', label: 'Happy Clients', icon: FiUsers },
    { number: '98%', label: 'Client Satisfaction', icon: FiAward },
    { number: '25+', label: 'Years Of Market Experience', icon: FiTarget }
  ];

  const values = [
    'Cutting-Edge Financial Technology',
    'Comprehensive Market Research',
    'Expert Investment Guidance',
    'Secure & Compliant Operations',
    'Dedicated Customer Support'
  ];

  return (
    <Box bg={bgColor}>
      {/* Hero Section */}
      <Box 
        bg={useColorModeValue('white', 'gray.800')} 
        py={20}
        position="relative"
        overflow="hidden"
      >
        <Container maxW="container.xl">
          <VStack spacing={6} align="center" textAlign="center">
            <Badge colorScheme="brand" fontSize="sm" px={3} py={1}>
              About Us
            </Badge>
            <Heading 
              size="2xl" 
              bgGradient={gradientText}
              bgClip="text"
              mb={4}
            >
              Empowering Financial Growth
            </Heading>
            <Text
              fontSize="xl"
              color={useColorModeValue('gray.600', 'gray.400')}
              maxW="2xl"
              lineHeight="tall"
            >
              We are dedicated to revolutionizing the mutual fund distribution landscape
              by providing innovative solutions that empower distributors and enhance
              client experiences.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box py={16}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            {stats.map((stat, index) => (
              <StatBox key={index} {...stat} />
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Updated Mission Section */}
      <Box py={16} bg={useColorModeValue('white', 'gray.800')}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={16} alignItems="center">
            <VStack align="start" spacing={8}>
              <VStack align="start" spacing={4}>
                <Badge colorScheme="brand">Our Mission</Badge>
                <Heading 
                  size="xl"
                  bgGradient={gradientText}
                  bgClip="text"
                >
                  Transforming Financial Services
                </Heading>
              </VStack>
              <Text fontSize="lg" color={useColorModeValue('gray.600', 'gray.400')} lineHeight="tall">
                Our mission is to empower financial distributors with cutting-edge technology
                and comprehensive support, enabling them to provide exceptional service to their clients.
              </Text>
              <List spacing={4}>
                {values.map((value, index) => (
                  <ListItem key={index}>
                    <HStack>
                      <ListIcon as={FiCheck} color="brand.500" w={6} h={6} />
                      <Text fontSize="lg">{value}</Text>
                    </HStack>
                  </ListItem>
                ))}
              </List>
            </VStack>
            <Box
              position="relative"
              _after={{
                content: '""',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '140%',
                height: '140%',
                bg: 'brand.500',
                opacity: 0.1,
                borderRadius: 'full',
                zIndex: 0,
              }}
            >
              <Image
                src="/images/investment-services.svg"
                alt="Financial Services Growth"
                w="full"
                h="auto"
                maxH="400px"
                rounded="2xl"
                shadow="2xl"
                position="relative"
                zIndex={1}
                objectFit="contain"
              />
            </Box>
          </SimpleGrid>
        </Container>
      </Box>
    </Box>
  );
};

export default About; 